import { AskConfirmation, buttonsContinueCancel } from '../../../webmodule-common/other/ui/modal-confirmation';
import { DataBinding } from '../../../webmodule-common/other/ui/databinding/databinding';
import { DataTracker, FieldType } from '../../../webmodule-common/other/ui/databinding/data-tracker';
import { FormInputAssistant } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant';
import { html } from 'lit';
import { isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { LangQuoteItemCommentModal } from '../../../localization/scripts/quotes/quote-item-comment-modal-lang';
import { LitBaseModal } from '../../../webmodule-common/other/ui/modal/modal-factory-lit';
import { newGuid } from '../../../webmodule-common/other/api/guid';
import { when } from 'lit/directives/when.js';

type ModalResult = 'update' | 'cancel';
type NoteType = 'comment' | 'commentToManufacturer';

export interface QuoteItemCommentModalResult {
  result: ModalResult;
  comment?: string | null;
  copyToCounterpart?: boolean;
}

export async function getCommentEditModal(
  modalSize = 'modal-xl',
  modalTitle = 'Comment',
  readonly: boolean = false,
  comment?: string | null,
  noteType?: NoteType,
  counterpartComment?: string | null
): Promise<QuoteItemCommentModalResult> {
  const modal = new QuoteItemCommentModal(readonly, modalSize, modalTitle, comment, noteType, counterpartComment);
  await modal.showModal();

  return {
    result: modal.result,
    comment: modal.comment,
    copyToCounterpart: modal.copyToCounterpart
  };
}

class QuoteItemCommentModal extends LitBaseModal {
  comment?: string | null = null;
  result: ModalResult = 'cancel';
  copyToCounterpart: boolean = false;

  protected _modalTitle: string;
  protected elementId = newGuid();
  private _readonly: boolean = false;
  private _noteType?: NoteType;
  private _counterpartComment?: string | null;
  private _dataBinding: DataBinding;
  private _dataTracker: DataTracker;
  private _forms: FormInputAssistant;

  constructor(
    readonly = false,
    modalSize = 'modal-xl',
    modalTitle = LangQuoteItemCommentModal.commentTitle(),
    comment?: string | null,
    noteType?: NoteType,
    counterpartComment?: string | null
  ) {
    super();

    this.comment = comment;
    this._modalTitle = modalTitle;
    this._noteType = noteType;
    this._counterpartComment = counterpartComment;

    this._dataBinding = new DataBinding(this.ui, this.elementId, input => `${input}-${this.elementId}`);

    this._dataTracker = new DataTracker(this._dataBinding);
    this._dataTracker.addObjectBinding(() => this, 'comment', 'comment', FieldType.string, true);
    this._dataTracker.addObjectBinding(() => this, 'copyToCounterpart', 'copyToCounterpart', FieldType.boolean, true);

    this._forms = new FormInputAssistant(this._dataTracker);
    this._readonly = readonly;
    this.modal.events.modalSize = modalSize;
  }

  override bodyTemplate() {
    const showCopyCheckbox = this._noteType && !this._readonly;
    const checkboxLabel =
      this._noteType === 'comment'
        ? LangQuoteItemCommentModal.copyToManufacturer()
        : LangQuoteItemCommentModal.copyToClient();
    const helperText =
      this._noteType === 'comment'
        ? LangQuoteItemCommentModal.copyToManufacturerHelper()
        : LangQuoteItemCommentModal.copyToClientHelper();

    return html`
      <form class="modal-confirmation-reason form-one-col">
        <div class="row">
          <div class="form-column">
            ${when(
              this._readonly,
              () => html`${this._forms.noteReadonly('comment', undefined, 15)}`,
              () => html`${this._forms.note('comment', undefined, 5000, undefined, undefined, 15)}`
            )}
          </div>
        </div>
        ${when(
          showCopyCheckbox,
          () => html`
            <div class="row">
              <div class="form-column">
                ${this._forms.checkbox('copyToCounterpart', checkboxLabel)}
                <div class="text-muted d-flex align-items-center gap-2">
                  <webmodule-icon library="fa" name="fas-circle-info" class="text-primary"></webmodule-icon>
                  <span class="small">${helperText}</span>
                </div>
              </div>
            </div>
          `
        )}
      </form>
    `;
  }

  protected prepareForSave() {
    this._dataTracker.applyChangeToValue();
  }

  title() {
    return this._modalTitle;
  }

  override footerTemplate() {
    const cancelEvent = () => {
      this.result = 'cancel';
      this.comment = null;
      this.hideModal();
    };

    const updateEvent = async () => {
      this.prepareForSave();

      // If the copy checkbox is checked, show confirmation dialog only if counterpart has existing content
      if (this.copyToCounterpart && !isEmptyOrSpace(this._counterpartComment)) {
        const confirmTitle =
          this._noteType === 'comment'
            ? LangQuoteItemCommentModal.confirmCopyToManufacturerTitle()
            : LangQuoteItemCommentModal.confirmCopyToClientTitle();
        const confirmMessage =
          this._noteType === 'comment'
            ? LangQuoteItemCommentModal.confirmCopyToManufacturerMessage()
            : LangQuoteItemCommentModal.confirmCopyToClientMessage();

        const confirmed = await AskConfirmation(confirmMessage, buttonsContinueCancel(), undefined, confirmTitle);

        if (!confirmed) {
          // User canceled - don't close the modal
          return;
        }
      }

      // User confirmed or checkbox was not checked - proceed with update
      this.result = 'update';
      this.hideModal();
    };

    const updateButton = () => {
      if (this._readonly) return html``;
      return this._forms.buttonPrimary(LangQuoteItemCommentModal.updateButton(), {
        events: { click: updateEvent },
        readonly: this._readonly
      });
    };

    return html`
      ${this._forms.buttonDefault(LangQuoteItemCommentModal.closeButton(), { events: { click: cancelEvent } })}
      ${updateButton()}
    `;
  }
}
